import React, { useState } from "react";
import api from "../../chat/api";
import Shell from "./CopilotCardShell";

/* What the knowledge base could not settle on its own.
 *
 * Two kinds of question, and they need different controls:
 *
 *   CONFLICT  two of our own documents disagree on a fact this bid has to
 *             state. The values are shown WITH their source document, because
 *             "20 or 50?" is unanswerable but "20 (Profile.pdf p.3) or 50
 *             (CapStmt.docx p.1)?" usually answers itself.
 *   GAP       the RFP asks for something the KB does not contain. Free text,
 *             because there is nothing to choose between.
 *
 * EVERY QUESTION IS SKIPPABLE, and that is deliberate. A checkpoint you cannot
 * get past stops being a safeguard and becomes a wall between the user and the
 * thing they asked for. Skipping is a real answer — it is recorded, and the
 * writer is told not to invent the value AND not to flag its absence, so the
 * proposal writes around it instead of leaving a hole with an apology in it.
 *
 * The card only ever renders when there is something to ask; the page checks
 * first and skips straight past when the list comes back empty.
 */

const SKIP = "__skip__";
const CUSTOM = "__custom__";

const ProposalClarifyCard = ({ message, onDone }) => {
  const { projectId, questions = [] } = message;

  // { [id]: { value, custom } } — `custom` holds free text for a conflict whose
  // answer is neither of the offered values.
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (id, patch) =>
    setAnswers((a) => ({ ...a, [id]: { ...(a[id] || {}), ...patch } }));

  const resolve = (q) => {
    const a = answers[q.id] || {};
    if (a.value === SKIP) return { id: q.id, question: q.question, answer: "", skipped: true };
    const text = a.value === CUSTOM ? (a.custom || "").trim() : (a.value || a.custom || "").trim();
    return { id: q.id, question: q.question, answer: text, skipped: !text };
  };

  const submit = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/v1/rfp/folders/${projectId}/proposal/clarify/answers/`, {
        answers: questions.map(resolve),
      });
      onDone?.(message);
    } catch {
      // Saving answers must never be the thing that blocks a proposal. The row
      // says what happened, and the button can be pressed again.
      setError("Couldn't save those answers.");
      setSaving(false);
    }
  };

  const answered = questions.filter((q) => {
    const r = resolve(q);
    return r.answer || r.skipped;
  }).length;

  return (
    <Shell
      icon={
        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M9.5 9a2.5 2.5 0 1 1 3.2 2.4c-.6.2-.9.7-.9 1.3v.4" />
          <path d="M12 17h.01" />
        </svg>
      }
      title="A few things to confirm before I write"
      width={640}
      meta={
        <span className="text-[11.5px] font-semibold tabular-nums text-[#8E4187]">
          {answered}/{questions.length}
        </span>
      }
      footer={
        <div className="flex items-center justify-between gap-3">
          <p className="text-[12px] text-[#77707E]">
            Anything you skip is left out rather than guessed.
          </p>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="inline-flex flex-none items-center gap-2 rounded-xl bg-gradient-to-br from-[#A64E9D] to-[#8E4187] px-4 py-2 text-[13.5px] font-semibold text-white ring-1 ring-inset ring-white/[0.16] shadow-[0_1px_2px_rgba(24,16,28,0.10),0_8px_18px_-10px_rgba(142,65,135,0.65)] transition-[transform,box-shadow,filter] duration-200 hover:-translate-y-[1px] hover:brightness-[1.04] active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-55 disabled:hover:transform-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] focus-visible:ring-offset-2 motion-reduce:transition-none"
          >
            {saving ? "Saving…" : "Use these and write"}
          </button>
        </div>
      }
    >
      <div className="space-y-3.5 px-4 py-3.5">
        {error && (
          <p role="alert" className="text-[12.5px] text-[#B91C1C]">
            {error}
          </p>
        )}

        {questions.map((q) => {
          const a = answers[q.id] || {};
          return (
            <div key={q.id} className="rounded-xl border border-gray-200 px-3.5 py-3">
              <p className="text-[13px] font-semibold leading-snug text-[#18101C]">{q.question}</p>
              {q.kind === "gap" && q.why && (
                <p className="mt-1 text-[12px] leading-relaxed text-[#77707E]">{q.why}</p>
              )}

              {q.kind === "conflict" ? (
                <div className="mt-2 space-y-1.5">
                  {(q.values || []).map((v, i) => {
                    const on = a.value === v.value;
                    return (
                      <button
                        key={`${q.id}-${i}`}
                        type="button"
                        onClick={() => set(q.id, { value: v.value })}
                        className={`flex w-full items-start gap-2 rounded-lg border px-2.5 py-1.5 text-left transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none ${
                          on
                            ? "border-[#A04A97] bg-[#A04A97]/[0.05]"
                            : "border-gray-200 hover:border-[#A04A97]/40"
                        }`}
                      >
                        <span
                          aria-hidden="true"
                          className={`mt-[3px] flex h-3 w-3 flex-none items-center justify-center rounded-full border ${
                            on ? "border-[#A04A97]" : "border-gray-300"
                          }`}
                        >
                          {on && <span className="h-1.5 w-1.5 rounded-full bg-[#A04A97]" />}
                        </span>
                        <span className="min-w-0">
                          <span className="block text-[12.5px] text-[#18101C]">{v.value}</span>
                          {v.source && (
                            <span className="block text-[11.5px] text-[#9CA3AF]">{v.source}</span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => set(q.id, { value: CUSTOM })}
                    className={`w-full rounded-lg border px-2.5 py-1.5 text-left text-[12.5px] transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none ${
                      a.value === CUSTOM
                        ? "border-[#A04A97] bg-[#A04A97]/[0.05] text-[#18101C]"
                        : "border-gray-200 text-[#52525B] hover:border-[#A04A97]/40"
                    }`}
                  >
                    Neither — the right value is something else
                  </button>
                  {a.value === CUSTOM && (
                    <input
                      autoFocus
                      value={a.custom || ""}
                      onChange={(e) => set(q.id, { custom: e.target.value })}
                      placeholder="The correct value"
                      className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 font-outfit text-[12.5px] text-[#18101C] placeholder:text-[#9CA3AF] focus:border-[#A04A97] focus:outline-none"
                    />
                  )}
                </div>
              ) : (
                <input
                  value={a.custom || ""}
                  onChange={(e) => set(q.id, { custom: e.target.value, value: "" })}
                  placeholder="Your answer"
                  className="mt-2 w-full rounded-lg border border-gray-200 px-2.5 py-1.5 font-outfit text-[12.5px] text-[#18101C] placeholder:text-[#9CA3AF] focus:border-[#A04A97] focus:outline-none"
                />
              )}

              <button
                type="button"
                onClick={() => set(q.id, { value: SKIP, custom: "" })}
                className={`mt-1.5 text-[11.5px] transition-colors duration-200 focus:outline-none focus-visible:underline motion-reduce:transition-none ${
                  a.value === SKIP
                    ? "font-semibold text-[#8E4187]"
                    : "text-[#9CA3AF] hover:text-[#77707E]"
                }`}
              >
                {a.value === SKIP ? "Leaving this out" : "Leave it out"}
              </button>
            </div>
          );
        })}
      </div>
    </Shell>
  );
};

export default ProposalClarifyCard;
